import { JSX, Suspense, useMemo, useState } from "react";

import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { Box, Grid, Paper, ButtonGroup, IconButton, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import MetricHeader from "src/components/MetricHeader/MetricHeader";
import MetricLeftBlock from "src/components/MetricHeader/MetricLeftBlock";
import ComponentId from "src/constants/componentId.enum";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import SeriesType from "src/constants/seriesType.enum";
import sharedStyles from "src/constants/styles";
import { mapSiteFootfallEvolution } from "src/lib/chart.helper";
import { buildChartData, getDateTime, getIntervalFromSelectedTab, getMeasuresData, getToday } from "src/lib/date.helper";
import { formatNum, getCurrentTotalCount, getDifference, getExpectedVisitors, getPastTotalCount } from "src/lib/number.helpers";
import useIntervalTabsStore from "src/store/intervalTabs.store";
import { LocationModel } from "src/types/location";
import { MeasuresItemizedItemDto, type MeasuresItemizedDto } from "src/types/locationChartDto";

import FootfallEvolutionChart from "./FootfallEvolutionChart";

const getStyles = () => ({
  fullWidth: { xs: 12 },
  chartContainer: { mt: 2, height: 400 },
});

function FootfallEvolution({ selectedLocation, componentId }: { selectedLocation: LocationModel; componentId: string }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { componentStates, setView } = useIntervalTabsStore();

  const stateForPassedId = componentStates[componentId] ?? {
    selectedTab: IntervalTab.Day,
    selectedDate: undefined,
  };
  const stateForCurrentComponent = componentStates[ComponentId.FootfallEvolution] ?? { view: SeriesType.Bar };

  const componentState = {
    ...stateForPassedId,
    view: stateForCurrentComponent.view,
  };

  const selectedDate = componentState.selectedDate ? dayjs(componentState.selectedDate) : null;

  const [chartType, setChartType] = useState(componentState.view);

  const { data: measuresDataToday } = useHistoryChartQuery(selectedLocation.Id, IntervalType.Day, selectedDate, MeasureType.Count);

  const { data: measuresDataWeek } = useHistoryChartQuery(selectedLocation.Id, IntervalType.Week, selectedDate, MeasureType.Count);

  const { data: measuresDataMonth } = useHistoryChartQuery(selectedLocation.Id, IntervalType.Month, selectedDate, MeasureType.Count);

  const { data: measuresDataYear } = useHistoryChartQuery(selectedLocation.Id, IntervalType.IsoYear, selectedDate, MeasureType.Count);

  const measuresData = useMemo(
    () => getMeasuresData(componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear),
    [componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear]
  ) as MeasuresItemizedDto;

  let chartData: MeasuresItemizedItemDto[] = [];

  if (measuresData) {
    chartData = buildChartData(measuresData, "itemized", componentState.selectedTab) as MeasuresItemizedItemDto[];
    measuresData.data = chartData;
  }

  const diff = useMemo(() => getDifference(measuresData), [measuresData]);
  const totalCurrentValue = useMemo(() => (measuresData?.data ? getCurrentTotalCount(measuresData?.data) : 0), [measuresData]);
  const totalPastValue = useMemo(() => (measuresData?.data ? getPastTotalCount(measuresData?.data) : 0), [measuresData]);

  const intervalType = getIntervalFromSelectedTab(componentState.selectedTab);

  const { data: measuresWholeDayData } = useHistoryChartQuery(
    selectedLocation.Id,
    intervalType,
    dayjs(measuresData?.pastDateRange.start),
    MeasureType.Count
  );

  const chartWholeDayData = useMemo(() => measuresWholeDayData?.data ?? [], [measuresWholeDayData?.data]);

  const pastPeriodWholeDaySum = useMemo(() => getCurrentTotalCount(chartWholeDayData), [chartWholeDayData]);

  const expectedVisitors = useMemo(() => {
    if (measuresData?.data?.length === 0) {
      return pastPeriodWholeDaySum;
    }
    return getExpectedVisitors(totalPastValue, totalCurrentValue, pastPeriodWholeDaySum);
  }, [measuresData?.data, totalPastValue, totalCurrentValue, pastPeriodWholeDaySum]);

  const dataRows = useMemo(
    () => (measuresData ? mapSiteFootfallEvolution(measuresData, selectedLocation.LocalName, intervalType) : []),
    [measuresData, selectedLocation.LocalName, intervalType]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <CardHeader header={t(`footfall evolution`)} dateTime={getDateTime(measuresData, componentState.selectedTab, selectedDate ?? getToday(), t)} />
      <MetricHeader
        leftContent={
          <MetricLeftBlock
            variant='footfall'
            totalCurrentValue={formatNum(totalCurrentValue)}
            totalPastValue={formatNum(totalPastValue)}
            diff={diff}
            pastLabel={t("previous year")}
          />
        }
        rightContent={
          <ButtonGroup color='secondary' variant='outlined' sx={sharedStyles(theme).secondaryInverseIconButtonGroup}>
            <IconButton
              sx={sharedStyles(theme).secondaryInverseButtonGroupButton}
              size='small'
              disabled={chartType === SeriesType.Line}
              onClick={() => {
                setChartType(SeriesType.Line);
                setView(ComponentId.FootfallEvolution, SeriesType.Line);
              }}
            >
              <ShowChartIcon />
            </IconButton>

            <IconButton
              sx={sharedStyles(theme).secondaryInverseButtonGroupButton}
              size='small'
              disabled={chartType === SeriesType.Bar}
              onClick={() => {
                setChartType(SeriesType.Bar);
                setView(ComponentId.FootfallEvolution, SeriesType.Bar);
              }}
            >
              <BarChartIcon />
            </IconButton>

            <Box display='flex' alignItems='center' ml={1}>
              <DownloadCSV componentId={ComponentId.FootfallEvolution} locationName={selectedLocation.LocalName} rows={dataRows} />
            </Box>
          </ButtonGroup>
        }
      />

      <Grid container spacing={2}>
        <Grid size={getStyles().fullWidth} sx={getStyles().chartContainer}>
          <Suspense fallback={<ChartSkeleton />}>
            {measuresData ? (
              <FootfallEvolutionChart
                measuresData={measuresData}
                selectedTab={componentState.selectedTab}
                chartType={chartType as SeriesType}
                expectedVisitors={expectedVisitors}
              />
            ) : (
              <ChartSkeleton />
            )}
          </Suspense>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default FootfallEvolution;
